import './styles.scss';

import { useMutation } from '@apollo/client';
import { Button } from '@ets-global/b2c-website-ui';
import { fromJS } from 'immutable';
import React, { useEffect, useRef, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { useHistory, useLocation } from 'react-router';
import * as yup from 'yup';

import HeaderMeta from '../../components/HeaderMeta';
import PrepTools from '../../components/PrepTools';
import { CREATE_PREP_TOOL_PURCHASE } from '../../graphql/mutations/Purchase/createPrepToolPurchase';
import { PREPTOOLS_VERSIONS } from '../../graphql/queries';
import errorsHandler from '../../helpers/errorsHandler';
import { getParams } from '../../helpers/router';
import { useBasketCookie, useDataLayer, useNotification, useQuery, useRouter } from '../../hooks';
import { paths } from '../../paths';
import Filters from './filters';

const PrepToolsPage = ({ purchase, purchaseRefetch, registrationId }) => {
  const history = useHistory();
  const location = useLocation();
  const { lang, countryEmoIso2, generatePath } = useRouter();
  const { pushEventLoadedPurchaseFunnel } = useDataLayer();
  const { getPromoCodeOrVoucher } = useBasketCookie();
  const promoCodeOrVoucher = getPromoCodeOrVoucher();
  const pageLoaded = useRef(false);
  const { newNotification } = useNotification();
  const { t } = useTranslation();
  const [isCreatingPurchase, setIsCreatingPurchase] = useState(false);
  const [createPurchase] = useMutation(CREATE_PREP_TOOL_PURCHASE);

  const {
    data: { preparationToolVersion = [] },
    loading,
  } = useQuery(PREPTOOLS_VERSIONS, {
    variables: {
      country: countryEmoIso2,
      lang,
      preparationToolVersionInput: {
        discount:
          promoCodeOrVoucher?.discountCode && !promoCodeOrVoucher?.isVoucher
            ? { discountCode: promoCodeOrVoucher.discountCode, referrer: promoCodeOrVoucher.referrer }
            : null,
      },
    },
  });

  const testTypeFamilies = preparationToolVersion.reduce((reduction, value) => {
    if (reduction.indexOf(value['relatedFamily']['testTypeFamilyName']) < 0) {
      reduction.push(value['relatedFamily']['testTypeFamilyName']);
    }

    return reduction;
  }, []);

  const constraints = {
    testTypeFamilyName: yup
      .string()
      .required()
      .default(() => 'all'),
    book: yup.boolean().default(true),
    online: yup.boolean().default(true),
  };
  const schemaValidation = yup.object().shape(constraints);

  const filters = fromJS(schemaValidation.cast(getParams(location.search)));
  const image = {
    url: require('../../assets/images/banner/prep-tools/mobile.jpg'),
    dimensions: {
      width: 350,
      height: 350,
    },
  };

  useEffect(() => {
    if (!pageLoaded.current) {
      pageLoaded.current = true;
      pushEventLoadedPurchaseFunnel({
        type: 'prep tools',
        stepName: 'prep tools',
        stepNumber: 1,
      });

      if (promoCodeOrVoucher?.prepToolIds && !registrationId) {
        setIsCreatingPurchase(true);

        createPurchase({
          variables: {
            prepTools: promoCodeOrVoucher.prepToolIds.map((id) => ({ id: id, quantity: 1 })),
            discount: promoCodeOrVoucher.discountCode,
            country: countryEmoIso2,
            referrer: promoCodeOrVoucher.referrer,
          },
        })
          .then(({ data: { createPrepToolPurchase } }) => {
            if (createPrepToolPurchase.__typename === 'PurchaseCannotBeCreated') {
              newNotification({
                content: t(`purchase-summary.error.${createPrepToolPurchase.reason}`),
                type: 'error',
              });

              return;
            }

            history.replace(
              generatePath(paths.PREP_TOOLS_BY_REGISTRATION_ID, {
                registrationId: createPrepToolPurchase.registrationId,
              })
            );
          })
          .catch((error) => {
            errorsHandler({ error, newNotification, t });
            setIsCreatingPurchase(false);
          });
      }
    }
  }, [purchase]);

  return (
    <>
      <HeaderMeta page={'prep-tools'} meta={{ image }} />
      <article className={'container container--full container--blue'}>
        <div className={'container container--padding'}>
          <section className="prep-tools-list__header">
            <Filters filters={fromJS(filters)} testTypeFamilies={testTypeFamilies} />
            <Button
              disabled={purchase === undefined}
              className={'prep-tools-list__button'}
              onClick={() =>
                history.push(
                  generatePath(paths.PURCHASE_SUMMARY, {
                    registrationId: purchase ? purchase.registrationId : null,
                  })
                )
              }
              data-cypress="buying-process.next-step"
              aria-label={t('session-registration.cross-selling.finalize')}
            >
              <Trans>session-registration.cross-selling.finalize</Trans>
            </Button>
          </section>
          <PrepTools
            loading={loading || isCreatingPurchase}
            preparationToolVersions={fromJS(preparationToolVersion)}
            purchase={fromJS(purchase || {})}
            filters={filters}
            purchaseRefetch={purchaseRefetch}
          />
        </div>
      </article>
    </>
  );
};

export default PrepToolsPage;
